<div class="payments-page">

  <!-- Page Header -->
  <div class="page-header">
    <h1 class="page-title">Payment Transaction</h1>
    <p class="page-subtitle">Monitor your revenue and transaction history</p>
  </div>

  <!-- Stat Cards -->
  <div class="stats-carousel-wrapper">
    <div class="stats-scroll" #statsScroll>
      @if (loadingStats) {
        @for (_ of [1,2,3,4]; track $index) {
          <div class="stat-card skeleton-card">
            <div class="stat-body">
              <div class="skel skel-label"></div>
              <div class="skel skel-value"></div>
              <div class="skel skel-change"></div>
            </div>
            <div class="skel skel-icon-circle"></div>
          </div>
        }
      } @else {
        @for (card of statCards; track card.label) {
          <div class="stat-card" [class.gradient]="card.gradient">
            <div class="stat-body">
              <p class="stat-label" [class.white-text]="card.gradient">{{ card.label }}</p>
              <h2 class="stat-value" [class.white-text]="card.gradient"
                [countUp]="card.numericValue ?? 0"
                [prefix]="card.label === 'Transactions' || card.label === 'Success Rate' ? '' : '$'"
                [suffix]="card.label === 'Success Rate' ? '%' : ''"
                [useComma]="true"
                [useKMB]="card.label !== 'Transactions' && card.label !== 'Success Rate'"
              [duration]="2000"></h2>
              <p class="stat-change" [class.white-text]="card.gradient" [class.negative]="!card.positive && !card.gradient">
                {{ card.change }}
              </p>
            </div>
            <div class="stat-icon-wrap" [style.background]="card.iconBg">
              <i class="pi" [ngClass]="card.icon" [style.color]="card.iconColor"></i>
            </div>
          </div>
        }
      }
    </div>
  </div>

  <!-- Revenue Overview -->
  <div class="chart-card">
    <div class="chart-header">
      <h3>Revenue Overview</h3>
      <div class="period-tabs">
        @for (p of periods; track p.value) {
          <button class="period-btn" [class.active]="activePeriod.value === p.value" (click)="setPeriod(p)">
            {{ p.label }}
          </button>
        }
      </div>
    </div>
    <div class="chart-wrap">
      @if (loadingChart) {
        <div class="chart-skeleton">
          <div class="skel-chart-bars">
            @for (_ of [40,65,30,80,55,90,45,70,35,60,85,50]; track $index) {
              <div class="skel-bar" [style.height.%]="_"></div>
            }
          </div>
        </div>
      } @else if (revenueChartData) {
        <p-chart type="line" [data]="revenueChartData" [options]="revenueChartOptions"
          [style]="{ height: '300px', width: '100%' }">
        </p-chart>
      }
    </div>
  </div>

  <!-- Action Bar -->
  <div class="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-1">
    <div class="d-flex align-items-center gap-3">
      <!-- Search -->
      <app-search-box tooltipText="Search By firstName, lastName, email, and description" [value]="filters.search" (search)="onSearch($event)"></app-search-box>

      <!-- Dates -->
      <div class="date-filter icon-only-datepicker">
        <p-datepicker [(ngModel)]="rangeDates" selectionMode="range" dateFormat="dd-mm-yy" [showClear]="true"
          (onSelect)="onDateFilter()" (onClear)="clearFilters()" [showIcon]="true" placeholder="Select date range">
        </p-datepicker>
      </div>

      <!-- Bulk Delete -->
      @if (hasSelection()) {
        <button class="btn btn-icon btn-delete-bulk"
          style="background: #fee2e2; border: none; border-radius: 8px; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; cursor: pointer;"
          (click)="deleteSelected()" title="Delete Selected">
          <i class="pi pi-trash" style="font-size: 1.1rem; color: #dc2626;"></i>
        </button>
      }
    </div>

    <div class="d-flex align-items-center gap-3 ms-auto">
      <div class="d-flex align-items-center gap-2 border rounded px-1 header-icon-actions-wrapper"
        style="height: 38px; border-color: #e5e7eb !important;">
        <button type="button" class="btn btn-icon p-1 header-icon-action-btn"
          style="border: none; background: transparent; cursor: pointer;" (click)="loadTransactions()"
          title="Refresh">
          <i class="pi pi-refresh" style="font-size: 1.1rem; color: #6b7280;"></i>
        </button>
        <button type="button" class="btn btn-icon p-1 tooltip-wrap header-icon-action-btn"
          style="border: none; background: transparent; cursor: pointer;"
          (click)="toggleIncludeDeleted()">
          <i class="pi pi-asterisk" style="font-size: 1.1rem;"
          [style.color]="includeDeleted ? '#ec4899' : '#6b7280'"></i>
          <span class="tooltip-box">Include Mark Deleted</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Recent Transactions -->
  <div class="table-container shadow">


    @if (loadingTransactions) {
      <div class="skeleton-table">
        @for (_ of [1,2,3,4,5]; track $index) {
          <div class="skel-row">
            <div class="skel-user">
              <div class="skel skel-avatar"></div>
              <div class="skel-user-text">
                <div class="skel skel-name"></div>
                <div class="skel skel-email"></div>
              </div>
            </div>
            <div class="skel skel-cell"></div>
            <div class="skel skel-cell-sm"></div>
            <div class="skel skel-cell-sm"></div>
            <div class="skel skel-badge"></div>
          </div>
        }
      </div>
    } @else {
      <table class="user-table">
        <thead>
          <tr>
            <th class="checkbox-col">
              <input type="checkbox" [(ngModel)]="allSelected" (change)="toggleSelectAll()" />
            </th>
            <th class="sortable" (click)="onSort('user')">
              User <span class="sort-indicator" [class.active]="currentSortField === 'user'">{{ getSortIcon('user') }}</span>
            </th>
            <th class="sortable" (click)="onSort('paymentType')">
              Payment Type <span class="sort-indicator" [class.active]="currentSortField === 'paymentType'">{{ getSortIcon('paymentType') }}</span>
            </th>
            <th class="sortable" (click)="onSort('amount')">
              Amount <span class="sort-indicator" [class.active]="currentSortField === 'amount'">{{ getSortIcon('amount') }}</span>
            </th>
            <th class="sortable" (click)="onSort('date')">
              Date <span class="sort-indicator" [class.active]="currentSortField === 'date'">{{ getSortIcon('date') }}</span>
            </th>
            <th class="sortable" (click)="onSort('status')">
              Status <span class="sort-indicator" [class.active]="currentSortField === 'status'">{{ getSortIcon('status') }}</span>
            </th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (t of transactions; track t._id) {
            <tr [class.selected]="t.selected" [class.deleted-row]="!!t.deletedAt">
              <td class="checkbox-col">
                <input type="checkbox" [(ngModel)]="t.selected" (change)="onTransactionSelect()" />
              </td>
              <td>
                <div class="user-cell">
                  <div class="user-avatar" [style.background]="getAvatarBg($index)" [style.color]="getAvatarColor($index)">
                    @if (getProfileImage(t.userInfo, t._id)) {
                      <img [src]="getProfileImage(t.userInfo, t._id)"
                        [alt]="getDisplayName(t.userInfo)" class="avatar-img" (error)="handleAvatarError(t._id)" />
                    }
                    @if (!getProfileImage(t.userInfo, t._id)) {
                      <span>
                        {{ getInitials(t.userInfo) }}
                      </span>
                    }
                  </div>
                  <div>
                    <p class="user-name">{{ getDisplayName(t.userInfo) | titlecase }}</p>
                    <p class="user-email">{{ t.userInfo.email || 'N/A' }}</p>
                  </div>
                </div>
              </td>
              <td>
                <div class="payment-type-cell">
                  <span class="pay-icon" [ngClass]="'pay-' + resolvePaymentType(t).type">
                    @if (resolvePaymentType(t).type === 'card') {
                      <i class="pi pi-credit-card"></i>
                    } @else if (resolvePaymentType(t).type === 'paypal') {
                      <span class="paypal-p">P</span>
                    } @else {
                      <i class="pi pi-building"></i>
                    }
                  </span>
                  <span class="pay-label">{{ resolvePaymentType(t).label }}</span>
                </div>
              </td>
              <td class="amount-cell">{{ t.amountFormatted }}</td>
              <td class="date-cell">{{ t.createdAt | date: 'MMM d, yyyy' }}</td>
              <td>
                <span class="status-badge completed">Completed</span>
              </td>
              <td class="text-center">
                <div class="action-btns d-flex justify-content-center">
                  <button class="btn btn-view" title="View details" (click)="viewTransaction(t)">View</button>
                </div>
              </td>
            </tr>
          }
          @empty {
          <tr>
            <td colspan="7" class="empty-state">No transactions found.</td>
          </tr>
        }
      </tbody>
    </table>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="pagination-info">
        <span>Showing {{ showingStart }} to {{ showingEnd }} of {{ totalCount }} Entries</span>
      </div>
      <div class="d-flex align-items-center gap-3">
        <div class="d-flex align-items-center gap-2">
          <label class="page-size-label mb-0" for="pageSizeSelect">Page Size:</label>
          <select id="pageSizeSelect" [ngModel]="limit" (ngModelChange)="onPageSizeChange($event)" class="form-select"
            style="width: 70px;">
            @for (size of pageSizes; track size) {
              <option [value]="size">
                {{ size }}
              </option>
            }
          </select>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline pagination-btn" style="white-space: nowrap;"
            [disabled]="page === 1 || loadingTransactions || transactions.length === 0" (click)="prevPage()">
            &lt; Prev
          </button>
          <button class="btn btn-outline pagination-btn" style="white-space: nowrap;"
            [disabled]="!hasMore || loadingTransactions" (click)="nextPage()">
            Next &gt;
          </button>
        </div>
      </div>
    </div>
  }
</div>

</div>

<!-- Transaction Details Modal -->
@if (showDetailsModal && selectedTransaction) {
  <app-transaction-details-modal [visible]="showDetailsModal" [transaction]="selectedTransaction"
    (onClose)="closeDetailsModal()" (refunded)="onTransactionRefunded()" />
  }